/**
 * Pending Transfers List
 * @see Sprint 28 - US-28.6
 */

import { useNavigate } from 'react-router-dom';
import { ArrowLeftRight, ChevronRight } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter, Badge, Skeleton, EmptyState } from '@/components/ui';
import { useFormatDate } from '@/hooks';
import { usePendingTransfers } from '../hooks';

const STATUS_BADGES: Record<string, { label: string; variant: 'warning' | 'primary' }> = {
  PENDING: { label: 'En attente', variant: 'warning' },
  SHIPPED: { label: 'En transit', variant: 'primary' },
};

export function PendingTransfersList() {
  const navigate = useNavigate();
  const { formatDateShort } = useFormatDate();
  const { data, isLoading, isError } = usePendingTransfers();

  if (isError) return null;

  const items = data?.data ?? [];
  const total = data?.pagination?.total ?? 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Transferts a receptionner</CardTitle>
            <CardDescription>Transferts entre sites en cours</CardDescription>
          </div>
          {total > 0 && (
            <Badge variant="primary">{total} transferts</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={ArrowLeftRight}
            title="Aucun transfert en cours"
            size="sm"
          />
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            {items.map((transfer) => {
              const badge = STATUS_BADGES[transfer.status];
              return (
                <div
                  key={transfer.id}
                  className="rounded-lg border border-neutral-200 p-3 hover:bg-neutral-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/stock/transferts/${transfer.id}`)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-sm text-dark-500">
                      {transfer.transferNumber}
                    </span>
                    {badge && (
                      <Badge variant={badge.variant}>{badge.label}</Badge>
                    )}
                  </div>
                  <div className="text-xs text-neutral-500 space-y-1">
                    <div>De: {transfer.sourceSite.name}</div>
                    <div>Vers: {transfer.destinationSite.name}</div>
                    <div className="flex items-center justify-between pt-1">
                      <span>{transfer.lineCount} articles</span>
                      <span>{formatDateShort(transfer.createdAt)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
      {total > 0 && (
        <CardFooter>
          <button
            className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium"
            onClick={() => navigate('/stock/transferts?status=PENDING,SHIPPED')}
          >
            Voir tous les transferts
            <ChevronRight className="h-4 w-4" />
          </button>
        </CardFooter>
      )}
    </Card>
  );
}
